import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Key, Loader2, X, Check, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/modules/collector_v2/lib/api";

const ADMIN_TOKEN_STORAGE_KEY = "ppk_admin_token";
const POLL_INTERVAL_MS = 3000;
const MAX_POLL_S = 180;

/**
 * Botón "🔑 Refrescar rdkey" para una cuenta del collector.
 * Implementa flujo completo:
 *   1. Pide X-Admin-Token (cached en localStorage)
 *   2. POST refresh → recibe job_id
 *   3. Polling cada 3s hasta status terminal
 *   4. Muestra estado en vivo + instrucciones para el admin
 *   5. Botón cancelar
 *   6. Manejo de todos los estados de error
 */
export function RdkeyRefreshButton({ accountName, uid, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("idle"); // idle|token|running|done|error
  const [adminToken, setAdminToken] = useState(
    () => (typeof window !== "undefined" && localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY)) || ""
  );
  const [job, setJob] = useState(null);
  const [err, setErr] = useState("");
  const pollTimerRef = useRef(null);

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  useEffect(() => () => stopPolling(), []);

  const handleOpen = () => {
    setOpen(true);
    setErr("");
    setJob(null);
    setStep(adminToken ? "running_pre" : "token");
  };

  const handleStart = async () => {
    if (!adminToken) {
      setErr("Ingresá el X-Admin-Token");
      return;
    }
    // Cache token para próximas veces
    try {
      localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, adminToken);
    } catch {
      // sin localStorage no es bloqueante
    }
    setStep("running");
    setErr("");
    try {
      const r = await axios.post(
        `${API}/v2/collector/accounts/${accountName}/rdkey/refresh`,
        {},
        { headers: { "X-Admin-Token": adminToken } }
      );
      setJob(r.data);
      pollTimerRef.current = setInterval(() => pollJob(r.data.job_id), POLL_INTERVAL_MS);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 403) {
        setErr("Token inválido. Verificá X-Admin-Token.");
        setStep("token");
      } else if (status === 409) {
        const exJob = typeof detail === "object" ? detail : {};
        setErr(`Ya hay un refresh activo (job ${exJob.existing_job_id}). Cancelalo primero.`);
        setStep("error");
      } else if (status === 503) {
        setErr(typeof detail === "string" ? detail : "PPPOKER_ADMIN_TOKEN no configurado.");
        setStep("error");
      } else {
        setErr(typeof detail === "string" ? detail : e.message);
        setStep("error");
      }
    }
  };

  const pollJob = async (jobId) => {
    try {
      const r = await axios.get(
        `${API}/v2/collector/rdkey-jobs/${jobId}`,
        { headers: { "X-Admin-Token": adminToken } }
      );
      setJob(r.data);
      const terminal = [
        "completed", "failed", "cancelled", "timeout",
        "sniffer_crashed", "invalid_format", "rdkey_unchanged",
        "verification_failed", "orphaned",
      ];
      if (terminal.includes(r.data.status)) {
        stopPolling();
        if (r.data.status === "completed") {
          setStep("done");
          toast.success(`rdkey de ${accountName} actualizado`, {
            description: `Login verificado: nuevo rdkey ${r.data.new_rdkey_prefix}`,
          });
          onSuccess?.();
        } else {
          setStep("error");
          setErr(r.data.error || `status: ${r.data.status}`);
        }
      }
      // Safety: si elapsed > MAX_POLL_S y todavía no terminal, parar
      if (r.data.elapsed_s && r.data.elapsed_s > MAX_POLL_S) {
        stopPolling();
        setStep("error");
        setErr("Tiempo de polling agotado. Revisá estado en la lista de jobs.");
      }
    } catch (e) {
      console.warn("poll error", e);
    }
  };

  const handleCancel = async () => {
    if (!job?.job_id) {
      stopPolling();
      setOpen(false);
      return;
    }
    try {
      await axios.post(
        `${API}/v2/collector/rdkey-jobs/${job.job_id}/cancel`,
        {},
        { headers: { "X-Admin-Token": adminToken } }
      );
    } catch (e) {
      console.warn(e);
    }
    stopPolling();
    setOpen(false);
    setStep("idle");
  };

  const handleClose = () => {
    stopPolling();
    setOpen(false);
    setStep("idle");
    setJob(null);
    setErr("");
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="h-7 px-2.5 mt-1.5 text-[11px] gap-1 border-blue-300 text-blue-700 hover:bg-blue-50 hover:text-blue-800 hover:border-blue-400"
        onClick={handleOpen}
        data-testid={`rdkey-refresh-btn-${accountName}`}
        title="Refrescar rdkey de esta cuenta"
      >
        <Key className="h-3.5 w-3.5" />
        Refrescar rdkey
      </Button>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : handleClose())}>
        <DialogContent className="sm:max-w-[520px]" data-testid="rdkey-refresh-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-4 w-4" />
              Refrescar rdkey de {accountName} (UID {uid})
            </DialogTitle>
            <DialogDescription>
              Lanza un sniffer en el VPS para capturar un nuevo rdkey desde la VM.
            </DialogDescription>
          </DialogHeader>

          {step === "token" && (
            <div className="space-y-3 py-2" data-testid="rdkey-token-step">
              <Label>X-Admin-Token</Label>
              <Input
                type="password"
                value={adminToken}
                onChange={(e) => setAdminToken(e.target.value)}
                placeholder="Pegá el PPPOKER_ADMIN_TOKEN configurado en .env"
                data-testid="rdkey-admin-token-input"
              />
              <p className="text-xs text-muted-foreground">
                Se guarda en localStorage para no pedirlo de nuevo.
              </p>
              {err && (
                <p className="text-xs text-rose-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {err}
                </p>
              )}
            </div>
          )}

          {step === "running_pre" && (
            <div className="py-3 space-y-2" data-testid="rdkey-pre-step">
              <p className="text-sm">
                Al confirmar:
              </p>
              <ol className="text-xs space-y-1.5 list-decimal pl-5 text-muted-foreground">
                <li>Se lanza el sniffer en el VPS (puerto 4000)</li>
                <li>Andá a la VM por VNC/RDP</li>
                <li>Cerrá PPPoker si está abierto</li>
                <li>Abrí PPPoker y loguéate como <strong>{accountName}</strong> (UID <strong>{uid}</strong>)</li>
                <li>El sistema captura el rdkey, lo aplica al .env (con backup) y verifica login</li>
              </ol>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2 rounded">
                ⏱️ Timeout: 2 minutos. Si no se detecta login válido se cancela solo.
              </p>
            </div>
          )}

          {step === "running" && job && (
            <div className="py-3 space-y-3" data-testid="rdkey-running-step">
              <div className="flex items-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                <span className="font-semibold">{job.status}</span>
                {job.elapsed_s != null && (
                  <span className="text-xs text-muted-foreground">
                    · {Math.round(job.elapsed_s)}s
                  </span>
                )}
              </div>
              {job.log_step && (
                <p className="text-xs text-muted-foreground italic">{job.log_step}</p>
              )}
              <div className="text-xs bg-blue-50 border border-blue-200 p-3 rounded space-y-1">
                <p className="font-semibold text-blue-900">📋 Pasos en la VM:</p>
                <ol className="list-decimal pl-5 text-blue-800 space-y-0.5">
                  <li>VNC/RDP a la VM Windows</li>
                  <li>Cerrá PPPoker (si está abierto)</li>
                  <li>Abrí PPPoker y loguéate con <strong>{accountName}</strong></li>
                </ol>
              </div>
              {job.pcap_path && (
                <p className="text-[10px] text-muted-foreground font-mono">
                  pcap: {job.pcap_path}
                </p>
              )}
            </div>
          )}

          {step === "done" && job && (
            <div className="py-3 space-y-2" data-testid="rdkey-done-step">
              <div className="flex items-center gap-2 text-sm text-emerald-700">
                <Check className="h-4 w-4" />
                <span className="font-semibold">rdkey actualizado correctamente</span>
              </div>
              <div className="text-xs bg-emerald-50 border border-emerald-200 p-3 rounded space-y-0.5">
                <p>✅ Nuevo rdkey: <span className="font-mono">{job.new_rdkey_prefix}</span></p>
                <p>✅ Login verificado contra el servidor PPPoker</p>
                <p>✅ Worker {accountName} debería reactivarse en segundos</p>
              </div>
            </div>
          )}

          {step === "error" && (
            <div className="py-3 space-y-2" data-testid="rdkey-error-step">
              <div className="flex items-center gap-2 text-sm text-rose-700">
                <X className="h-4 w-4" />
                <span className="font-semibold">{job?.status || "Error"}</span>
              </div>
              <p className="text-xs bg-rose-50 border border-rose-200 p-3 rounded text-rose-700">
                {err || "Error desconocido"}
              </p>
              {job?.error && job.error !== err && (
                <p className="text-[10px] text-muted-foreground">{job.error}</p>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            {step === "token" && (
              <>
                <Button variant="ghost" onClick={handleClose}>Cancelar</Button>
                <Button onClick={() => setStep("running_pre")} disabled={!adminToken}>
                  Continuar
                </Button>
              </>
            )}
            {step === "running_pre" && (
              <>
                <Button variant="ghost" onClick={handleClose}>Cancelar</Button>
                <Button onClick={handleStart} data-testid="rdkey-start-btn">
                  Iniciar captura
                </Button>
              </>
            )}
            {step === "running" && (
              <Button variant="destructive" onClick={handleCancel} size="sm">
                Cancelar captura
              </Button>
            )}
            {(step === "done" || step === "error") && (
              <Button onClick={handleClose}>Cerrar</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
